/**
 * Standalone Orval mutator for the Vue admin panel.
 *
 * Provides an auth-aware axios instance that:
 *   1. Injects Bearer token from cookies on every request.
 *   2. Unwraps `response.data.content` so generated functions return the
 *      inner payload instead of the `*ResponseContent` wrapper.
 *   3. Handles 401 → refresh token → retry original request.
 *   4. Clears session and reloads on irrecoverable auth failure.
 *   5. Shows a toast on generic API errors.
 *
 * Intentionally does NOT import from any *Repository class – this prevents
 * the circular-dependency chain that exists in the legacy index.ts.
 */

import Axios, { type AxiosRequestConfig } from 'axios';
import { useCookies } from 'vue3-cookies';
import { setAllCookies, refreshTokenIsValid } from '@/services/utils';
import { useToast } from 'vuestic-ui';
import type { TLoginApiResponse } from '@/types/users';

// Mirrors CookiesName from @/api/index.ts – duplicated intentionally so that
// mutator.ts has no dependency on index.ts (which imports LoginRepository,
// creating a circular chain).
const CookiesName = {
  token: 'accessToken',
  tokenExpiresAt: 'tokenExpiresAt',
  refreshToken: 'refreshToken',
  refreshTokenExpiresAt: 'refreshTokenExpiresAt',
} as const;

const { cookies } = useCookies();
const { init: notify } = useToast();

type RetryableRequestConfig = AxiosRequestConfig & { _retry?: boolean };

const isAuthDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    const byStorage = window.localStorage.getItem('lop-auth-debug') === '1';
    const byQuery = new URLSearchParams(window.location.search).has(
      'authDebug',
    );
    return byStorage || byQuery;
  } catch {
    return false;
  }
};

const authDebugLog = (label: string, payload?: unknown) => {
  if (!isAuthDebugEnabled()) return;
  console.groupCollapsed(`[auth-debug] ${label}`);
  if (payload !== undefined) {
    console.log(payload);
  }
  console.groupEnd();
};

const isInvalidAccessTokenContextError = (error: unknown) => {
  if (!Axios.isAxiosError(error)) return false;
  const detail = error.response?.data?.detail;
  return (
    error.response?.status === 400 &&
    typeof detail === 'string' &&
    detail.toLowerCase().includes('access token is not valid in this context')
  );
};

const SERVER_URL = 'https://lop.di-dev.ru';

export const axiosInstance = Axios.create({
  baseURL:  SERVER_URL
});

// ── request interceptor ────────────────────────────────────────────────────

axiosInstance.interceptors.request.use((config) => {
  // Remove empty / null / blank query params so the backend does not receive
  // stray undefined/null values.
  Object.keys(config.params || {}).forEach((key) => {
    const v = config.params[key];
    if (v === undefined || v === null || (typeof v === 'string' && !v.length)) {
      delete config.params[key];
    }
  });

  const token = cookies.get(CookiesName.token);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// ── standalone refresh (no LoginRepository dependency) ────────────────────

// Redirect to login and clear all auth cookies (called on irrecoverable auth failure).
const redirectToLogin = () => {
  Object.values(CookiesName).forEach((name) => cookies.remove(name));
  window.location.href = '/login';
};

// Singleton in-flight refresh promise.
// Prevents multiple simultaneous 401 responses from each triggering their
// own refresh call (second call would reuse an already-consumed refresh token).
let activeRefreshPromise: Promise<void> | null = null;

async function performTokenRefresh(expiredToken: string): Promise<void> {
  const refreshToken = cookies.get(CookiesName.refreshToken);
  const baseURL =  SERVER_URL
  

  authDebugLog('refresh:start', {
    hasExpiredToken: !!expiredToken,
    hasRefreshToken: !!refreshToken,
    endpoint: `${baseURL}/api/v1/auth/refresh`,
  });

  // The backend requires both accessToken and refreshToken per the OpenAPI spec:
  // RefreshJsonWebTokenRequest { accessToken: string; refreshToken: string }
  const res = await Axios.post(`${baseURL}/api/v1/auth/refresh`, {
    accessToken: expiredToken,
    refreshToken,
  });

  authDebugLog('refresh:success', {
    status: res.status,
    hasContent: !!res.data?.content,
  });

  const credentials: TLoginApiResponse = res.data?.content ?? res.data;
  setAllCookies(credentials);
}

// ── response interceptor ──────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
  // Unwrap `{ content: T }` wrapper – every successful response from this
  // backend is shaped as `{ content: <actual payload> }`.
  (response) => response.data?.content ?? response.data,

  async (error) => {
    const originalRequest = (error.config ?? {}) as RetryableRequestConfig;
    const url: string = originalRequest.url ?? '';

    authDebugLog('response:error', {
      url,
      status: error?.response?.status,
      code: error?.code,
      detail: error?.response?.data?.detail,
      payload: error?.response?.data,
    });

    // If the refresh call itself failed, clear all tokens and go to login.
    if (url.includes('/auth/refresh')) {
      authDebugLog(
        'refresh:failed, redirecting to login',
        error?.response?.data,
      );
      if (isAuthDebugEnabled()) {
        // Pause here in DevTools to inspect the refresh error payload.
        debugger;
      }
      redirectToLogin();
      return Promise.reject(error);
    }

    const shouldTryRefresh =
      error?.response?.status === 401 ||
      isInvalidAccessTokenContextError(error);

    if (shouldTryRefresh && !originalRequest._retry) {
      originalRequest._retry = true;

      // Capture the token before removing it – refresh endpoint needs it.
      const expiredToken: string = cookies.get(CookiesName.token) ?? '';
      cookies.remove(CookiesName.token);
      cookies.remove(CookiesName.tokenExpiresAt);

      if (refreshTokenIsValid()) {
        try {
          // Reuse an in-flight refresh so that concurrent 401 responses
          // don't each fire their own refresh request.
          if (!activeRefreshPromise) {
            activeRefreshPromise = performTokenRefresh(expiredToken);
          }
          await activeRefreshPromise;
          activeRefreshPromise = null;

          // Retry the original request with the new access token.
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${cookies.get(
            CookiesName.token,
          )}`;
          authDebugLog('request:retry', { url, retry: true });
          const retried = await axiosInstance(originalRequest);
          return Promise.resolve(retried);
        } catch (refreshError) {
          activeRefreshPromise = null;
          authDebugLog('refresh:exception', {
            status: Axios.isAxiosError(refreshError)
              ? refreshError.response?.status
              : undefined,
            payload: Axios.isAxiosError(refreshError)
              ? refreshError.response?.data
              : refreshError,
          });
          if (isAuthDebugEnabled()) {
            // Pause here to inspect refresh exception in DevTools.
            debugger;
          }
          redirectToLogin();
          return Promise.reject(error);
        }
      } else {
        authDebugLog('refresh:skip, refresh token invalid');
        if (isAuthDebugEnabled()) {
          debugger;
        }
        redirectToLogin();
      }
    } else {
      notify({
        message: error.response?.data?.detail || 'Произошла ошибка',
        color: 'danger',
      });
    }

    return Promise.reject(error);
  },
);

// ── Orval mutator export ───────────────────────────────────────────────────

/**
 * Custom mutator consumed by Orval-generated code.
 * Signature matches the axios-client mutator contract:
 *   <T>(config: AxiosRequestConfig) => Promise<T>
 *
 * The response interceptor above already unwraps `.content`, so at runtime
 * `T` resolves to the inner payload type (not the `*ResponseContent` wrapper).
 */
export const apiClient = <T>(config: AxiosRequestConfig): Promise<T> =>
  axiosInstance(config) as unknown as Promise<T>;

export default apiClient;
